// The agent's questions before it acts (builder §15.3 `ask_user`): one to three multiple-choice questions, the recommended option marked;
// the person's choice goes back as the next message with the directive `answers` (stored as `extras.answers`). Only the latest unanswered
// card can be answered. A hairline card: one question is its title (the intro, when it says more than the answer above, under it); several
// follow the intro, each over its options. Each option is a native radio or checkbox drawn as a row, its label over its line of detail, so
// Tab reaches a question, the arrow keys move within it and Space picks. The footer holds the state as text and Send. Once answered, the
// options the person's reply names stay marked and the others recede. Styled by apps/assistant/src/econic/sidebar/ask.css (`.ea-ask*`),
// which the agent column and the agent's own pages both load. WP-SIDEBAR.
import { useId, useMemo, useState } from "react";
import { Check, MessageCircleQuestion } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import type { AskQuestion, AskUserEvent } from "../types";
import { useAgentFace } from "./faceSlot";

const k = (key: string) => `assistant:econic.sidebar.ask.${key}`;
/** A question as its answer line starts ("Chart type: Bars"): without its closing question mark. */
const stem = (question: string) => question.replace(/[?؟]\s*$/, "");

/** The options a reply names, per question: from the answer lines the card sends ("Question: A, B"), or one option's label alone. */
export function pickedFrom(ask: AskUserEvent, reply: string): Record<string, string[]> {
  const lines = reply.split("\n").map((l) => l.trim());
  return Object.fromEntries(ask.questions.map((q) => {
    const head = `${stem(q.question)}:`;
    const line = lines.find((l) => l.startsWith(head));
    const rest = line ? line.slice(head.length).trim() : ask.questions.length === 1 ? reply.trim() : "";
    const labels = q.options.map((o) => o.label).filter((l) => rest === l || `, ${rest}, `.includes(`, ${l}, `));
    return [q.id, q.multi ? labels : labels.slice(0, 1)];
  }));
}

export function AskUserCard({ ask, answerable, onAnswer, reply }: {
  ask: AskUserEvent;
  /** False once the person answered (or wrote anything after the questions), and while the reply that asks is still streaming. */
  answerable: boolean;
  /** The answers as one message, e.g. "Chart type: Bars · Period: Last 5 years". */
  onAnswer?: (text: string) => void;
  /** What the person sent after the questions, when the host knows it: the options it names are drawn as chosen. Null: nothing yet (a
   *  card that cannot be answered waits). Left out: the host does not say, and a card that cannot be answered reads as answered. */
  reply?: string | null;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const initial = useMemo(() => Object.fromEntries(ask.questions.map((q) => [q.id, q.options.filter((o) => o.recommended).slice(0, q.multi ? q.options.length : 1).map((o) => o.label)])), [ask]);
  const [picked, setPicked] = useState<Record<string, string[]>>(initial);
  const [sent, setSent] = useState(false);
  const state = answerable && onAnswer && !sent ? "open" : sent || reply != null || (reply === undefined && !answerable) ? "answered" : "waiting";
  const chosen = useMemo(() => state !== "answered" ? picked : reply != null ? pickedFrom(ask, reply) : sent ? picked : {}, [state, picked, reply, ask, sent]);
  const complete = ask.questions.every((q) => (picked[q.id] ?? []).length > 0);
  const toggle = (q: AskQuestion, label: string) => {
    setPicked((p) => {
      const now = p[q.id] ?? [];
      if (!q.multi) return { ...p, [q.id]: [label] };
      return { ...p, [q.id]: now.includes(label) ? now.filter((x) => x !== label) : [...now, label] };
    });
  };
  const send = () => {
    if (state !== "open" || !onAnswer || !complete) return;
    setSent(true);
    onAnswer(ask.questions.map((q) => `${stem(q.question)}: ${(picked[q.id] ?? []).join(", ")}`).join("\n"));
  };
  const single = ask.questions.length === 1 ? ask.questions[0]! : null;
  const titleId = `${uid}-title`;
  // The host's face of the agent asking (faceSlot.tsx), else the question glyph.
  const face = useAgentFace({ agentKey: null, state: state === "answered" ? "idle" : "asking", size: 24 });
  return (
    <div className="ea-ask" data-state={state} role={single ? undefined : "group"} aria-labelledby={single ? undefined : titleId}>
      <div className="ea-ask-h">
        {face ? <span className="face" aria-hidden>{face}</span> : <MessageCircleQuestion aria-hidden className="ic" />}
        <div className="tx">
          <p id={titleId} className="t" dir="auto">{single ? single.question : ask.intro || t(k("title"), { defaultValue: "A few questions before I change anything" })}</p>
          {single && ask.intro ? <p className="s" dir="auto">{ask.intro}</p> : null}
        </div>
      </div>
      {ask.questions.map((q, n) => (
        <fieldset key={q.id} className="ea-ask-q" disabled={state !== "open"} aria-labelledby={single ? titleId : undefined}>
          {single ? null : <legend dir="auto">{q.question}</legend>}
          <div className="opts">
            {q.options.map((o, i) => {
              const on = (chosen[q.id] ?? []).includes(o.label);
              const id = `${uid}-${n}-${i}`;
              return (
                <label key={o.label} className="opt" data-on={on || undefined} data-multi={q.multi || undefined}>
                  <input type={q.multi ? "checkbox" : "radio"} name={`${uid}-${n}`} checked={on} onChange={() => toggle(q, o.label)}
                    aria-labelledby={`${id}-l`} aria-describedby={o.detail ? `${id}-d` : undefined} />
                  <span className="mark" aria-hidden>{on && q.multi ? <Check /> : null}</span>
                  <span className="txt">
                    <span id={`${id}-l`} className="l" dir="auto">{o.label}{o.recommended ? <span className="rec">{t(k("recommended"), { defaultValue: "Recommended" })}</span> : null}</span>
                    {o.detail ? <span id={`${id}-d`} className="d" dir="auto">{o.detail}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
      {/* the op card's footer rule: the state as text at the start, the one action at the end */}
      <div className="ea-ask-f">
        {state === "answered" ? (
          <span className="st done"><Check aria-hidden />{t(k("answered"), { defaultValue: "Answered" })}</span>
        ) : (
          <>
            <span className="st">{t(k("hint"), { defaultValue: "Nothing changes until you answer" })}</span>
            {onAnswer ? <button type="button" className="ec-btn primary sm" disabled={state !== "open" || !complete} onClick={send}>{t(k("send"), { defaultValue: "Send answers" })}</button> : null}
          </>
        )}
      </div>
    </div>
  );
}
